// @flow
import ko from 'rgs-knockout';
import type { Observable, PureComputed } from 'rgs-knockout';
import _ from 'lodash';

import extendedKladrUtils from 'extended-kladr-utils';
import ObservableMap from 'observable-map';
import type { IObservableMap } from 'observable-map';

import { JSableOmitPrivate } from 'jsable';
import stringify from 'json-stable-stringify';


import type {
	ExtendedAddressInterface,
	ExtendedAddressHouseInterface,
	ExtendedAddressStreetInterface,
	ExtendedAddressSettlementInterface,
} from './contracts.flow';

import type {
	ExtendedAddressOptions,
	ExtendedAddressSettlementOptions,
	ExtendedAddressStreetOptions,
	ExtendedAddressHouseOptions,
	KladrModel,
	AddressJSModel,
	SettlementJSModel,
	StreetJSModel,
	HouseJSModel,
} from './types.flow';

class ExtendedAddressEntity
extends JSableOmitPrivate {
	Kladr: Observable<?string> | PureComputed<?string>;
	Name: Observable<?string> | PureComputed<?string>;

	constructor (dictionary: IObservableMap<string, string>) {
		super();

		this._dictionary = dictionary;

		this.Kladr = ko.observable()
			.extend({
				toFromJS: true,
				notify: 'always',
			});

		this.Name = ko.pureComputed({
			read: () => dictionary.get(this.Kladr()),
			write: name => {
				if (!name) {
					this.Kladr(null);
				} else {
					const id = dictionary.get(name);
					if (id) {
						this.Kladr(id);
					}
				}
			},
		})
			.extend({
				toJS: true,
			});
	}

	repr (): ?string {
		return this.Name();
	}
}

export class ExtendedAddressHouse extends ExtendedAddressEntity
	implements ExtendedAddressHouseInterface {
	_address: ExtendedAddressInterface;
	_dictionary: IObservableMap<{
		OuterKladr: string,
		Name: string,
	}, string>;

	constructor (options: ExtendedAddressHouseOptions) {
		const {
			address,
			dictionary,
		} = options;
		const obsoleteDictionary: IObservableMap<string, string> = new ObservableMap();

		super(obsoleteDictionary);

		this._address = address;
		this._dictionary = dictionary;

		this.Kladr = ko.map(this.Kladr, {
			mapRead: v => {
				const settlementKladr = this._address.Settlement.Kladr();
				const streetKladr = this._address.Street.Kladr();

				const houseKladr = v && settlementKladr && (
						//eslint-disable-next-line max-len
						(!this._address.HasStreets() && !streetKladr && extendedKladrUtils.directlyContains(settlementKladr, v))
						|| (streetKladr && extendedKladrUtils.contains(streetKladr, v)))
						? v
						:	null;

				return houseKladr;
			},
			mapWrite: v => v,
		})
			.extend({ toFromJS: true });

		this.Name = ko.map(ko.observable(), {
			mapWrite: (name: ? string) => {
				const kladr = dictionary.get(this._getDictionaryKey(name));

				this.Kladr(extendedKladrUtils.normalizeHouseKladr(kladr || null));

				if (!name) {
					this._address.Apartement(null);
				}

				return name;
			},
		})
			.extend({
				toFromJS: true,
			});
	}

	_getDictionaryKey (name: ?string): ?{ OuterKladr: string, Name: string } {
		const settlementKladr = this._address.Settlement.Kladr();
		const streetKladr = this._address.Street.Kladr();
		const hasStreets = this._address.HasStreets();

		const kladr = hasStreets ? streetKladr : settlementKladr;

		if (name && kladr) {
			return {
				OuterKladr: kladr,
				Name: name,
			};
		}

		return _.noop();
	}

	fromJS (js: ?HouseJSModel) {
		if (js) {
			const key = this._getDictionaryKey(js.Name);

			if (key && js.Kladr) {
				this._dictionary.set(key, js.Kladr);
			}
		}

		return super.fromJS(...arguments);
	}
}


export class ExtendedAddressStreet
extends ExtendedAddressEntity
implements ExtendedAddressStreetInterface {
	hasHouses: Observable<?boolean>;
	hasNoHouses: PureComputed<boolean>;

	constructor (options: ExtendedAddressStreetOptions) {
		const {
			address,
			dictionary,
		} = options;

		super(dictionary);

		this.hasHouses = ko.observable();

		this.hasNoHouses = ko.map(this.hasHouses, {
			mapRead: (v) => this.Kladr() && !_.isNil(v) && !v,
		});

		this.Kladr = ko.map(ko.observable(), {
			mapRead: (v: ? string) => {
				if (address.HasStreets()) {
					const settlementKladr = address.Settlement.Kladr();
					return settlementKladr && v && extendedKladrUtils.contains(settlementKladr, v)
						? v
						: null;
				}
				return null;
			},
			mapWrite: v => {
				if (!v) {
					address.House.Name(null);
				}
				return v;
			},
		})
			.extend({
				toFromJS: true,
			});
	}
	
	fromJS (js: ?KladrModel): StreetJSModel {
		if (js && js.Kladr && js.Name) {
			this._dictionary.bisetAbsent(
				js.Kladr,
				js.Name,
			);
		}

		return super.fromJS(...arguments);
	}
}

export class ExtendedAddressSettlement
extends ExtendedAddressEntity
implements ExtendedAddressSettlementInterface {
	_dictionary: IObservableMap<string, {
		Name: ?string,
		CityName: ?string,
		AreaName: ?string,
	}>;
	hasHouses: Observable<?boolean>;
	hasStreets: Observable<?boolean>;
	CityName: Observable<?string>;
	AreaName: Observable<?string>;

	hasNoStreetsOrHouses: PureComputed<boolean>;

	constructor (options: ExtendedAddressSettlementOptions) {
		const {
			dictionary = new ObservableMap(),
			address,
		} = options;
		const obsoleteDictionary: IObservableMap<string, string> = new ObservableMap();

		super(obsoleteDictionary);

		this._address = address;
		this._dictionary = dictionary;
		this._options = options;

		this.hasHouses = ko.observable();
		this.hasStreets = ko.observable();

		this.hasNoStreetsOrHouses = ko.pureComputed(() => {
			const hasNoHouses = !_.isNil(this.hasHouses()) && !this.hasHouses();
			const hasNoStreet = !_.isNil(this.hasStreets()) && !this.hasStreets();

			return Boolean(this.Kladr() && (hasNoStreet && hasNoHouses));
		});

		this.Kladr = ko.observable()
			.extend({
				toFromJS: true
			});

		this.Name = ko.map(ko.observable(), {
			mapRead: v => this._dictionary.get(this.Kladr()) ? v : null,
			mapWrite: (name: ?string) => {
				if (!name) {
					this.Kladr(null);
					this._address.Street.Name(null);
				} else {
					const id = this._dictionary.get({
						Name: name,
						CityName: this.CityName(),
						AreaName: this.AreaName(),
					});
					if (id) {
						this.Kladr(id);
					}
				}
				return name;
			},
		})
			.extend({
				toFromJS: true,
			});

		this.AreaName = ko.observable()
			.extend({
				toFromJS: true,
			});

		this.CityName = ko.observable()
			.extend({
				toFromJS: true,
			});

		// нужно длшя общего компонента, нигде кроме каско не нужно, переделать когда переделаем выборку на FullName
		this.settlementWithAreaRepr = ko.map(ko.observable(), {
			mapRead: () => {
				if (this.Name()) {
					return _([this.Name(), this.CityName(), this.AreaName()])
						.map(_.trim)
						.compact()
						.join(', ');
				}
				return '';
			},
			mapWrite: v => {
				if (!v) {
					this.AreaName(null);
					this.CityName(null);
					this.Name(null);
				}
				return v;
			},
		});
	}

	fromJS (js: ?SettlementJSModel): void {
		if (js && js.Kladr && js.Name) {
			this._dictionary.bisetAbsent(
				js.Kladr, {
					Name: js.Name,
					CityName: js.CityName,
					AreaName: js.AreaName,
				},
			);
		}

		return super.fromJS(js);
	}
}


export class ExtendedAddress
extends JSableOmitPrivate
implements ExtendedAddressInterface {
	constructor (
		options: ExtendedAddressOptions,
	) {

		const {
			kladr: {
				streets,
				settlements,
			}
		} = options.dictionaries;

		const addressOptions: ExtendedAddressOptions = {
			dictionaries: {
				kladr: {
					streets,
					settlements,
				},
			},
		};

		super(addressOptions);

		options = _.defaultsDeep({}, options, {
			dictionaries: {
				kladr: {
					houses: new ObservableMap({
						hash: (v) => stringify(v),
					}),
					streets: new ObservableMap({
						hash: (v) => stringify(v),
					}),
					settlements: new ObservableMap({
						hash: (v) => stringify(v),
					}),
				},
			},
		});

		this.Kladr = ko.pureComputed(() => {
			const kladr = this.House.Kladr() || this.Street.Kladr() || this.Settlement.Kladr();
			return kladr && extendedKladrUtils.normalizeHouseKladr(kladr);
		})
			.extend({
				toJS: true,
			});

		this.Settlement = new ExtendedAddressSettlement({
			address: this,
			dictionary: options.dictionaries.kladr.settlements,
		});

		this.HasStreets = ko.observable()
			.extend({
				toFromJS: true,
			});

		this.Street = new ExtendedAddressStreet({
			address: this,
			dictionary: options.dictionaries.kladr.streets,
		});

		this.House = new ExtendedAddressHouse({
			address: this,
			dictionary: options.dictionaries.kladr.houses,
		});

		this.Apartement = ko.observable()
			.extend({
				toFromJS: true,
			})
			.extend({
				repr: _.identity,
			});
	}

	repr (): string {
		const area = this.Settlement.AreaName();
		const city = this.Settlement.CityName();
		const name = this.Settlement.Name();
		const street = this.Street.repr();
		const house = this.House.repr();
		const apartement = this.Apartement.repr();

		return _([
			area,
			city,
			name,
			street,
			house,
			apartement,
		]).map(_.trim)
			.compact()
			.join(', ');
	}

	fromJS (js: AddressJSModel): void {
		super.fromJS(js);
		// когда происходит fromJS, street и settlement раскладываются позже чем House и он не может их найти
		if (js && js.House) {
			this.House.fromJS(js.House);
		}
	}
}
